import {
  useRef,
  useLayoutEffect,
  type ReactNode,
  useEffect,
  useState,
  lazy,
  Suspense,
} from 'react';
import { graphql } from 'gatsby';
import { createPortal } from 'react-dom';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Container, Wrapper, Content } from './Modal.styles';
import useModal from './hooks/useModal';
import CloseButton from './components/CloseButton/CloseButton';
import { type BlockHTMLId } from '@/utils/renderBlock';
const RichTextBlock = lazy(() => import('@/components/RichTextBlock/RichTextBlock'));

const Modal = ({
  modalElements,
  children,
  id,
}: Queries.MODALFragment & { children?: ReactNode } & BlockHTMLId) => {
  const modalID = modalElements.url_slug.value;
  const portalId = `modal-container-${modalID}`;
  const modalTitle = modalElements.title.value;
  const modularContentQuantity = modalElements.rich_text.modular_content.length;
  // If a modal has only one modular content item, we don't want to add padding to the modal.
  const hasPadding = modularContentQuantity > 1;
  const isVideoModal =
    modalElements.rich_text.modular_content.some(
      (item) => item?.__typename === 'kontent_item_video'
    ) && modularContentQuantity === 1;

  const ref = useRef(null);
  const { close, isOpen } = useModal();
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (document) {
      document.body.style.overflowY = isOpen() ? 'hidden' : 'scroll';
    }
  }, [isOpen, modalID]);

  useOnClickOutside(ref, () => {
    close(modalID);
  });

  useEffect(() => {
    if (document && !document.getElementById(portalId)) {
      const divElement = document.createElement('div');
      divElement.setAttribute('id', portalId);
      document.body.appendChild(divElement);

      setPortalContainer(divElement);

      return () => {
        document.body.removeChild(divElement);
      };
    }
  }, [portalId]);

  if (!isOpen(modalID) || !portalContainer) return null;

  return createPortal(
    <Container id={modalID}>
      <Wrapper id={id}>
        <Content role="dialog" aria-labelledby={modalTitle} hasPadding={hasPadding} ref={ref}>
          <CloseButton handleClick={() => close(modalID)} isVideoModal={isVideoModal} />
          {children}
          {Boolean(modalElements.rich_text.value.length) && (
            <Suspense>
              <RichTextBlock elements={{ rich_text_content: modalElements.rich_text }} />
            </Suspense>
          )}
        </Content>
      </Wrapper>
    </Container>,
    portalContainer
  );
};

export default Modal;

export const fragment = graphql`
  fragment MODAL on kontent_item_modal {
    modalElements: elements {
      title {
        value
      }
      url_slug {
        value
      }
      rich_text {
        value
        images {
          description
          height
          url
          image_id
          width
        }
        links {
          link_id
          codename
        }
        modular_content {
          ...VIDEO
          ...QUOTE
          ...CODE_SNIPPET
          ...TABLE
        }
      }
    }
  }
`;
